import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import {
  httpGetFormInstances,
  httpGetFormInstancesFromUser,
} from '../../Hooks/company/company.get.hooks';
import createNewFormInstance from '../../Forms/EditForm/createNewFormInstance.jsx';
import { httpEditFormInfo } from '../../Hooks/company/company.post.hooks';
import { httpAddCommentToFormInstance } from '../../Hooks/company/company.post.hooks';
import { httpDeleteCommentFromFormInstance } from '../../Hooks/company/company.delete.hooks';
import { httpToggleFlagFormInstance } from '../../Hooks/company/company.put.hooks';
import { httpToggleArchiveForm } from '../../Hooks/company/company.put.hooks';
import { httpDeleteFormInstance } from '../../Hooks/company/company.delete.hooks';
import { httpToggleFavoriteForm } from '../../Hooks/user/user.post.hooks';

const normalizeInstances = (instances = []) => {
  const byId = {};
  const ids = [];

  instances.forEach((instance) => {
    if (!instance?._id) return;

    byId[instance._id] = instance;
    ids.push(instance._id);
  });

  return { byId, ids };
};

const getInstanceFromPayload = (payload) => {
  return payload?.instance || payload?.formInstance || payload;
};

const sortComments = (instance) => {
  if (!instance || !Array.isArray(instance.comments)) return instance;

  return {
    ...instance,
    comments: [...instance.comments].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    ),
  };
};

const initialState = {
  form: null,

  instancesById: {},
  instanceIds: [],
  selectedInstanceId: null,

  users: [],
  favoritedForms: [],

  draftValues: {},
  dirtyFields: {},

  pdfBlobUrl: null,
  pdfBlobUrlSubmitted: null,

  fetchStatus: 'idle',
  createStatus: 'idle',
  saveStatus: 'idle',
  commentStatus: 'idle',
  deleteStatus: 'idle',
  pdfStatus: 'idle',
  flagStatus: 'idle',
  favoriteStatus: 'idle',
  archiveStatus: 'idle',

  error: null,
  saveError: null,
};

export const fetchFormInstances = createAsyncThunk(
  'formInstances/fetchFormInstances',
  async ({ userId, formId }, { rejectWithValue }) => {
    try {
      return await httpGetFormInstancesFromUser(userId, formId);
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch form instances');
    }
  }
);

export const fetchCompanyFormInstances = createAsyncThunk(
  'formInstances/fetchCompanyFormInstances',
  async ({ companyId, formId }, { rejectWithValue }) => {
    try {
      return await httpGetFormInstances(companyId, formId);
    } catch (err) {
      return rejectWithValue(
        err.message || 'Failed to fetch company instances'
      );
    }
  }
);

export const createFormInstance = createAsyncThunk(
  'formInstances/createFormInstance',
  async (payload, { rejectWithValue }) => {
    try {
      return await createNewFormInstance(payload);
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create form instance');
    }
  }
);

export const updateFormInstance = createAsyncThunk(
  'formInstances/updateFormInstance',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await httpEditFormInfo(payload);
      return getInstanceFromPayload(response);
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update form instance');
    }
  }
);

export const addCommentToInstance = createAsyncThunk(
  'formInstances/addCommentToInstance',
  async ({ formInstanceId, comment }, { rejectWithValue }) => {
    try {
      const response = await httpAddCommentToFormInstance(
        formInstanceId,
        comment
      );

      return getInstanceFromPayload(response);
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to add comment');
    }
  }
);

export const deleteCommentFromInstance = createAsyncThunk(
  'formInstances/deleteCommentFromInstance',
  async ({ formInstanceId, commentId }, { rejectWithValue }) => {
    try {
      const response = await httpDeleteCommentFromFormInstance(
        formInstanceId,
        commentId
      );

      return getInstanceFromPayload(response);
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to delete comment');
    }
  }
);

export const fetchFormPdf = createAsyncThunk(
  'formInstances/fetchFormPdf',
  async (pdfUrl, { rejectWithValue }) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      const res = await fetch(
        `${API_URL}/s3/pdf-proxy?url=${encodeURIComponent(pdfUrl)}`
      );

      if (!res.ok) throw new Error('Failed to fetch PDF');

      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch PDF');
    }
  }
);
export const fetchFormPdfSubmittedWithRetry = createAsyncThunk(
  'formInstances/fetchFormPdfSubmittedWithRetry',
  async (pdfUrl, { rejectWithValue }) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;

    const maxAttempts = 15;
    const delay = 4000;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const res = await fetch(
          `${API_URL}/s3/pdf-proxy?url=${encodeURIComponent(pdfUrl)}`
        );

        if (res.ok) {
          const blob = await res.blob();
          return URL.createObjectURL(blob);
        }

        if (attempt === maxAttempts) {
          throw new Error('PDF is not ready yet.');
        }
      } catch (err) {
        if (attempt === maxAttempts) {
          return rejectWithValue(
            err.message || 'Failed to fetch submitted PDF'
          );
        }
      }

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
);

export const fetchFormPdfSubmitted = createAsyncThunk(
  'formInstances/fetchFormPdfSubmitted',
  async (pdfUrl, { rejectWithValue }) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      const res = await fetch(
        `${API_URL}/s3/pdf-proxy?url=${encodeURIComponent(pdfUrl)}`
      );

      if (!res.ok) throw new Error('Failed to fetch submitted PDF');

      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch submitted PDF');
    }
  }
);

export const toggleFlagOnInstance = createAsyncThunk(
  'formInstances/toggleFlagOnInstance',
  async ({ formInstanceId, userId }, { rejectWithValue }) => {
    try {
      const response = await httpToggleFlagFormInstance(formInstanceId, userId);
      return getInstanceFromPayload(response);
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to toggle flag');
    }
  }
);

export const toggleArchiveOnForm = createAsyncThunk(
  'formInstances/toggleArchiveOnForm',
  async (formId, { rejectWithValue }) => {
    try {
      const response = await httpToggleArchiveForm(formId);
      return getInstanceFromPayload(response);
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to toggle archive');
    }
  }
);

export const deleteInstanceFromBackend = createAsyncThunk(
  'formInstances/deleteInstanceFromBackend',
  async ({ instanceId }, { rejectWithValue }) => {
    try {
      await httpDeleteFormInstance(instanceId);
      return instanceId;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to delete instance');
    }
  }
);

export const toggleFavoriteForm = createAsyncThunk(
  'formInstances/toggleFavoriteForm',
  async ({ formId, userId }, { rejectWithValue }) => {
    try {
      const response = await httpToggleFavoriteForm(formId, userId);

      if (response?.ok === false) {
        throw new Error('Failed to toggle favorite');
      }

      return { formId, userId };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to toggle favorite');
    }
  }
);

const formInstancesSlice = createSlice({
  name: 'formInstances',
  initialState,
  reducers: {
    setSelectedInstanceId(state, action) {
      state.selectedInstanceId = action.payload;
    },

    clearSelectedInstance(state) {
      state.selectedInstanceId = null;
      state.draftValues = {};
      state.dirtyFields = {};
      state.saveError = null;
      state.saveStatus = 'idle';
    },

    hydrateDraftValuesFromInstance(state, action) {
      const instance = action.payload;

      state.draftValues = instance?.data || instance?.values || {};
      state.dirtyFields = {};
      state.saveError = null;
      state.saveStatus = 'idle';
    },

    updateDraftFieldValue(state, action) {
      const { fieldId, value } = action.payload;

      state.draftValues[fieldId] = value;
      state.dirtyFields[fieldId] = true;
    },

    updateDraftValues(state, action) {
      const values = action.payload || {};

      Object.entries(values).forEach(([fieldId, value]) => {
        state.draftValues[fieldId] = value;
        state.dirtyFields[fieldId] = true;
      });
    },

    clearDirtyFields(state) {
      state.dirtyFields = {};
    },

    resetSaveStatus(state) {
      state.saveStatus = 'idle';
      state.saveError = null;
    },

    updateInstanceLocally(state, action) {
      const updated = getInstanceFromPayload(action.payload);
      if (!updated?._id) return;

      const previous = state.instancesById[updated._id] || {};
      state.instancesById[updated._id] = {
        ...previous,
        ...updated,
      };

      if (!state.instanceIds.includes(updated._id)) {
        state.instanceIds.unshift(updated._id);
      }
    },

    clearPdfBlobUrls(state) {
      state.pdfBlobUrl = null;
      state.pdfBlobUrlSubmitted = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchFormInstances.pending, (state) => {
        state.fetchStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchFormInstances.fulfilled, (state, action) => {
        const instances = action.payload?.instances || [];
        const normalized = normalizeInstances(instances);

        state.instancesById = normalized.byId;
        state.instanceIds = normalized.ids;

        state.form = action.payload?.form || null;
        state.users = action.payload?.users || [];
        state.favoritedForms = action.payload?.favoritedForms || [];

        state.fetchStatus = 'succeeded';
        state.error = null;
      })
      .addCase(fetchFormInstances.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.error = action.payload || action.error.message;
      })

      .addCase(fetchCompanyFormInstances.pending, (state) => {
        state.fetchStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchCompanyFormInstances.fulfilled, (state, action) => {
        const instances = action.payload?.instances || [];
        const normalized = normalizeInstances(instances);

        state.instancesById = normalized.byId;
        state.instanceIds = normalized.ids;

        state.form = action.payload?.form || null;
        state.users = action.payload?.users || [];
        state.favoritedForms = action.payload?.favoritedForms || [];

        state.fetchStatus = 'succeeded';
        state.error = null;
      })
      .addCase(fetchCompanyFormInstances.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.error = action.payload || action.error.message;
      })

      .addCase(createFormInstance.pending, (state) => {
        state.createStatus = 'loading';
        state.error = null;
      })
      .addCase(createFormInstance.fulfilled, (state, action) => {
        const instance = getInstanceFromPayload(action.payload);
        if (!instance?._id) return;

        state.instancesById[instance._id] = instance;
        state.instanceIds.unshift(instance._id);
        state.selectedInstanceId = instance._id;
        state.draftValues = instance.values || {};
        state.dirtyFields = {};

        state.createStatus = 'succeeded';
        state.error = null;
      })
      .addCase(createFormInstance.rejected, (state, action) => {
        state.createStatus = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchFormPdfSubmittedWithRetry.pending, (state) => {
        state.pdfStatus = 'loading';
        // state.pdfBlobUrlSubmitted = null;
      })
      .addCase(fetchFormPdfSubmittedWithRetry.fulfilled, (state, action) => {
        state.pdfStatus = 'succeeded';
        state.pdfBlobUrlSubmitted = action.payload;
      })
      .addCase(fetchFormPdfSubmittedWithRetry.rejected, (state) => {
        state.pdfStatus = 'failed';
        state.pdfBlobUrlSubmitted = null;
        // state.error = action.payload || action.error.message;
      })
      .addCase(updateFormInstance.pending, (state) => {
        state.saveStatus = 'loading';
        state.saveError = null;
      })
      .addCase(updateFormInstance.fulfilled, (state, action) => {
        const updated = getInstanceFromPayload(action.payload);
        if (!updated?._id) return;

        const previous = state.instancesById[updated._id] || {};

        state.instancesById[updated._id] = {
          ...previous,
          ...updated,
          createdBy: {
            ...previous.createdBy,
            ...updated.createdBy,
          },
        };

        if (!state.instanceIds.includes(updated._id)) {
          state.instanceIds.unshift(updated._id);
        }

        if (state.selectedInstanceId === updated._id) {
          // state.draftValues = updated.values || state.draftValues;
          state.dirtyFields = {};
        }

        state.saveStatus = 'succeeded';
        state.saveError = null;
      })
      .addCase(updateFormInstance.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.saveError = action.payload || action.error.message;
      })

      .addCase(addCommentToInstance.pending, (state) => {
        state.commentStatus = 'loading';
        state.error = null;
      })
      .addCase(addCommentToInstance.fulfilled, (state, action) => {
        const updated = sortComments(getInstanceFromPayload(action.payload));
        if (!updated?._id) return;

        state.instancesById[updated._id] = {
          ...state.instancesById[updated._id],
          ...updated,
        };

        state.commentStatus = 'succeeded';
        state.error = null;
      })
      .addCase(addCommentToInstance.rejected, (state, action) => {
        state.commentStatus = 'failed';
        state.error = action.payload || action.error.message;
      })

      .addCase(deleteCommentFromInstance.pending, (state) => {
        state.commentStatus = 'loading';
        state.error = null;
      })
      .addCase(deleteCommentFromInstance.fulfilled, (state, action) => {
        const updated = sortComments(getInstanceFromPayload(action.payload));
        if (!updated?._id) return;

        state.instancesById[updated._id] = {
          ...state.instancesById[updated._id],
          ...updated,
        };

        state.commentStatus = 'succeeded';
        state.error = null;
      })
      .addCase(deleteCommentFromInstance.rejected, (state, action) => {
        state.commentStatus = 'failed';
        state.error = action.payload || action.error.message;
      })

      .addCase(fetchFormPdf.pending, (state) => {
        state.pdfStatus = 'loading';
        state.pdfBlobUrl = null;
      })
      .addCase(fetchFormPdf.fulfilled, (state, action) => {
        state.pdfStatus = 'succeeded';
        state.pdfBlobUrl = action.payload;
      })
      .addCase(fetchFormPdf.rejected, (state) => {
        state.pdfStatus = 'failed';
        state.pdfBlobUrl = null;
        // state.error = action.payload || action.error.message;
      })

      .addCase(fetchFormPdfSubmitted.pending, (state) => {
        state.pdfStatus = 'loading';
        state.pdfBlobUrlSubmitted = null;
      })
      .addCase(fetchFormPdfSubmitted.fulfilled, (state, action) => {
        state.pdfStatus = 'succeeded';
        state.pdfBlobUrlSubmitted = action.payload;
      })
      .addCase(fetchFormPdfSubmitted.rejected, (state) => {
        state.pdfStatus = 'failed';
        state.pdfBlobUrlSubmitted = null;
        // state.error = action.payload || action.error.message;
      })

      .addCase(toggleFlagOnInstance.pending, (state) => {
        state.flagStatus = 'loading';
        state.error = null;
      })
      .addCase(toggleFlagOnInstance.fulfilled, (state, action) => {
        const updated = getInstanceFromPayload(action.payload);
        if (!updated?._id) return;

        state.instancesById[updated._id] = {
          ...state.instancesById[updated._id],
          ...updated,
        };

        state.flagStatus = 'succeeded';
      })
      .addCase(toggleFlagOnInstance.rejected, (state, action) => {
        state.flagStatus = 'failed';
        state.error = action.payload || action.error.message;
      })

      .addCase(toggleArchiveOnForm.pending, (state) => {
        state.archiveStatus = 'loading';
        state.error = null;
      })
      .addCase(toggleArchiveOnForm.fulfilled, (state, action) => {
        const updated = getInstanceFromPayload(action.payload);
        if (!updated?._id) return;

        state.instancesById[updated._id] = {
          ...state.instancesById[updated._id],
          ...updated,
        };

        state.archiveStatus = 'succeeded';
      })
      .addCase(toggleArchiveOnForm.rejected, (state, action) => {
        state.archiveStatus = 'failed';
        state.error = action.payload || action.error.message;
      })

      .addCase(deleteInstanceFromBackend.pending, (state) => {
        state.deleteStatus = 'loading';
        state.error = null;
      })
      .addCase(deleteInstanceFromBackend.fulfilled, (state, action) => {
        const id = action.payload;

        delete state.instancesById[id];
        state.instanceIds = state.instanceIds.filter(
          (instanceId) => instanceId !== id
        );

        if (state.selectedInstanceId === id) {
          state.selectedInstanceId = null;
          state.draftValues = {};
          state.dirtyFields = {};
        }

        state.deleteStatus = 'succeeded';
      })
      .addCase(deleteInstanceFromBackend.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.error = action.payload || action.error.message;
      })

      .addCase(toggleFavoriteForm.pending, (state) => {
        state.favoriteStatus = 'loading';
        state.error = null;
      })
      .addCase(toggleFavoriteForm.fulfilled, (state, action) => {
        const { formId } = action.payload;

        const current = state.instancesById[formId];

        if (current) {
          state.instancesById[formId] = {
            ...current,
            isFavorite: !current.isFavorite,
            isFavorited: !current.isFavorited,
          };
        }

        if (state.favoritedForms.includes(formId)) {
          state.favoritedForms = state.favoritedForms.filter(
            (id) => id !== formId
          );
        } else {
          state.favoritedForms.push(formId);
        }

        state.favoriteStatus = 'succeeded';
      })
      .addCase(toggleFavoriteForm.rejected, (state, action) => {
        state.favoriteStatus = 'failed';
        state.error = action.payload || action.error.message;
      });
  },
});

export const {
  setSelectedInstanceId,
  clearSelectedInstance,
  hydrateDraftValuesFromInstance,
  updateDraftFieldValue,
  updateDraftValues,
  clearDirtyFields,
  resetSaveStatus,
  updateInstanceLocally,
  clearPdfBlobUrls,
} = formInstancesSlice.actions;

export default formInstancesSlice.reducer;

export const selectFormInstancesState = (state) => state.formInstances;

export const selectInstancesArray = createSelector(
  selectFormInstancesState,
  (formInstances) =>
    formInstances.instanceIds
      .map((id) => formInstances.instancesById[id])
      .filter(Boolean)
);

export const selectSelectedInstance = createSelector(
  selectFormInstancesState,
  (formInstances) => {
    const id = formInstances.selectedInstanceId;
    if (!id) return null;

    return formInstances.instancesById[id] || null;
  }
);

export const selectDraftInstances = createSelector(
  selectInstancesArray,
  (instances) => instances.filter((instance) => instance.status !== 'submitted')
);

export const selectCompleteInstances = createSelector(
  selectInstancesArray,
  (instances) => instances.filter((instance) => instance.status === 'submitted')
);

export const selectHasDirtyFields = createSelector(
  selectFormInstancesState,
  (formInstances) => Object.keys(formInstances.dirtyFields || {}).length > 0
);

export const selectDirtyValues = createSelector(
  selectFormInstancesState,
  (formInstances) => {
    const dirtyValues = {};

    Object.keys(formInstances.dirtyFields || {}).forEach((fieldId) => {
      dirtyValues[fieldId] = formInstances.draftValues[fieldId];
    });

    return dirtyValues;
  }
);
